import { Box, Chip, Typography } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"

import type { QuotedProductListItem } from "./useGetQuotedProducts"

import { Button } from "@/components/common/Button"
import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import type { Product } from "@/graphql/codegen/graphql"
import { PRODUCT_STOCK_TYPE_CHIP_COLOR, PRODUCT_STOCK_TYPE_LABEL_MAP } from "@/screens/Products/constants"

interface UseQuotedProductColumnsProps {
  onAssociate: (productId: number) => void
}

export const useQuotedProductColumns = ({
  onAssociate,
}: UseQuotedProductColumnsProps): ColumnDef<QuotedProductListItem>[] => [
  {
    accessorKey: "productId",
    header: "Product ID",
    enableSorting: false,
    cell: ({ row }) => {
      const product = row.original.product
      return product ? <ProductStatusCell product={product as unknown as Product} /> : null
    },
  },
  {
    id: "description",
    header: "Description",
    enableSorting: false,
    cell: ({ row }) => (
      <Box className='flex flex-col'>
        <Typography variant='body2' className='font-medium text-gray-800'>
          {row.original.product?.companyProductDescription ?? null}
        </Typography>
      </Box>
    ),
  },
  {
    id: "supplier",
    accessorFn: (row) => row.product?.vendorDescription ?? "",
    header: "Supplier",
    enableSorting: false,
    cell: ({ row }) => row.original.product?.vendorDescription ?? null,
  },
  {
    id: "stockType",
    accessorFn: (row) => row.product?.stockType ?? "",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => {
      const stockType = row.original.product?.stockType
      if (!stockType) return null
      return (
        <Chip
          size='small'
          label={PRODUCT_STOCK_TYPE_LABEL_MAP[stockType]}
          color={PRODUCT_STOCK_TYPE_CHIP_COLOR[stockType]}
        />
      )
    },
  },
  {
    id: "quoteDate",
    header: "Quote Date",
    enableSorting: false,
    // TODO(AK-6130): QuotedProductItem exposes salesPipelineId but not the pipeline's quoteDate; validUntilDate is the quote's expiry, not its date.
    cell: () => null,
  },
  {
    id: "associate",
    header: "",
    size: 120,
    enableSorting: false,
    cell: ({ row }) => (
      <Button
        appearance='text'
        size='small'
        className='hover:bg-transparent'
        onClick={() => onAssociate(row.original.productId)}
        data-testid={`customer-orders.quoted-products-table.associate-button-${row.original.productId}`}
      >
        Associate
      </Button>
    ),
  },
]
